/**
 * Generic registry helper extracted from chart-registry +
 * compare-table-registry (R-18). Both modules carried byte-for-byte
 * identical maps, register/unregister loops with the stale-unsubscribe
 * guard, list snapshot, and test reset. The factory keeps the per-
 * consumer hook local (each takes a different payload signature), but
 * collapses the storage / lookup / reset surface.
 */
type RegistryHandle<TEntry> = {
  register: (entry: TEntry) => () => void;
  list: () => TEntry[];
  reset: () => void;
};

/**
 * Build a fresh module-level registry of entries keyed by a string id.
 * `extractId` reads the id off each entry; for the existing call sites
 * the entry shape is `{ id; title; … }` so the default `e => e.id`
 * works.
 */
export function createRegistry<TEntry extends { id: string }>(): RegistryHandle<TEntry> {
  const map = new Map<string, TEntry>();
  return {
    register(entry) {
      map.set(entry.id, entry);
      return () => {
        // Only delete the entry if it still belongs to this caller —
        // protects against stale unsubscribe calls clobbering a
        // re-mounted item.
        if (map.get(entry.id) === entry) map.delete(entry.id);
      };
    },
    list() {
      return Array.from(map.values());
    },
    reset() {
      map.clear();
    },
  };
}
